import { IconButton } from "@mui/material";
import intl from "react-intl-universal";
import { MdClose } from "react-icons/md";
import BR from "country-flag-icons/react/1x1/BR";
import US from "country-flag-icons/react/1x1/US";
import PY from "country-flag-icons/react/1x1/PY";
import DE from "country-flag-icons/react/1x1/DE";
import {
  formatCrossborderAmount,
  formatTransactionRoute,
  getCountry,
  getTransactionOperationFee,
} from "../constants";
import { CrossborderCopyButton } from "./CrossborderCopyButton";
import { CrossborderStatusBadge } from "./CrossborderStatusBadge";
import { CrossborderTrackingTimeline } from "./CrossborderTrackingTimeline";
import { PaymentRailChip } from "./PaymentRailChip";
import { PaymentRouteChips } from "./PaymentRouteChips";

const FLAG_MAP = { br: BR, us: US, py: PY, de: DE };

const CountryValue = ({ countryCode, countryLabel }) => {
  const Flag = FLAG_MAP[countryCode];
  const label = countryLabel ?? getCountry(countryCode)?.label;

  if (!label) return null;

  return (
    <span className="inline-flex items-center gap-1.5">
      {Flag ? <Flag className="h-4 w-4 shrink-0 rounded-full" title={countryCode?.toUpperCase()} /> : null}
      <span>{label}</span>
    </span>
  );
};

const DetailRow = ({ label, value, mono = false, copyValue, copyLabel }) => {
  if (value == null || value === "") return null;

  return (
    <div className="grid grid-cols-[minmax(5.5rem,auto)_1fr] items-center gap-x-3 py-1">
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <div className="flex min-w-0 items-center justify-end gap-1">
        <span
          className={`text-right text-sm text-brand-dark dark:text-gray-100 ${mono ? "font-mono text-xs break-all" : ""}`}
          title={typeof value === "string" ? value : undefined}
        >
          {value}
        </span>
        {copyValue ? (
          <CrossborderCopyButton value={copyValue} ariaLabel={copyLabel ?? `Copy ${label}`} />
        ) : null}
      </div>
    </div>
  );
};

const DetailTableRow = ({ label, value, mono = false }) => {
  if (value == null || value === "") return null;

  return (
    <div className="flex flex-col gap-0.5 border-b border-gray-100 py-2.5 last:border-b-0 dark:border-brand-dark-border sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <span
        className={`text-sm font-medium text-brand-dark dark:text-gray-100 sm:text-right ${mono ? "font-mono text-xs break-all" : ""}`}
      >
        {value}
      </span>
    </div>
  );
};

const TransactionHeader = ({ transaction, onClose }) => {
  const referenceId = transaction.referenceId ?? transaction.id;
  const routeLabel = formatTransactionRoute(transaction);
  const isPayout = transaction.type === "payout";

  return (
    <div className="flex items-start justify-between gap-2 border-b border-gray-100 px-4 py-2.5 dark:border-brand-dark-border sm:px-5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <div className="flex min-w-0 items-center gap-1">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              {intl.get("crossborder.detail.transaction")}
            </p>
            <h3 className="truncate font-mono text-sm text-brand-dark dark:text-gray-100">#{referenceId}</h3>
            <CrossborderCopyButton
              value={referenceId}
              ariaLabel={intl.get("crossborder.detail.copyTransactionId")}
            />
          </div>
          {!isPayout ? (
            <PaymentRouteChips
              originRailId={transaction.originRailId}
              destinationRailId={transaction.destinationRailId}
              fromCurrency={transaction.fromCurrency}
              toCurrency={transaction.toCurrency}
              fixedWidth={false}
            />
          ) : null}
          <span className="sr-only">{routeLabel}</span>
        </div>
      </div>
      {onClose ? (
        <IconButton aria-label={intl.get("common.close")} onClick={onClose} size="small" className="!-mr-1 !-mt-0.5">
          <MdClose />
        </IconButton>
      ) : null}
    </div>
  );
};

const DetailFooter = ({ onClose }) =>
  onClose ? (
    <div className="shrink-0 border-t border-gray-100 px-4 py-2 dark:border-brand-dark-border sm:px-5">
      <div className="flex justify-end">
        <button type="button" className="btn-outline !h-8 !min-h-8 !px-3 text-xs" onClick={onClose}>
          {intl.get("common.close")}
        </button>
      </div>
    </div>
  ) : null;

const useTransactionAmounts = (transaction) => {
  const senderAmount =
    transaction.senderAmount != null
      ? formatCrossborderAmount(
          transaction.senderAmount,
          transaction.senderAmountCurrency ?? transaction.fromCurrency
        )
      : formatCrossborderAmount(transaction.fromAmount, transaction.fromCurrency);

  const receiverAmount =
    transaction.receiverAmount != null
      ? formatCrossborderAmount(
          transaction.receiverAmount,
          transaction.receiverAmountCurrency ?? transaction.toCurrency
        )
      : formatCrossborderAmount(transaction.toAmount, transaction.toCurrency);

  const { amount: feeAmount, currencyId: feeCurrency } = getTransactionOperationFee(transaction);
  const operationFeeLabel = formatCrossborderAmount(feeAmount, feeCurrency);

  return { senderAmount, receiverAmount, operationFeeLabel };
};

const PayinDetailBody = ({ transaction }) => {
  const { senderAmount, receiverAmount, operationFeeLabel } = useTransactionAmounts(transaction);

  const originMethodLabel = (
    <PaymentRailChip railId={transaction.originRailId} currencyId={transaction.fromCurrency} />
  );
  const destinationMethodLabel = (
    <PaymentRailChip railId={transaction.destinationRailId} currencyId={transaction.toCurrency} />
  );

  const accountLabel = transaction.valutAccount ?? transaction.customerWallet;
  const originAccountLabel = transaction.originAccount ?? transaction.senderWallet;

  return (
    <div className="flex-1 overflow-y-auto px-4 py-3 sm:px-5">
      <div className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
        <section>
          <h4 className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-brand-dark dark:text-gray-100">
            {intl.get("crossborder.detail.payinDetails")}
          </h4>
          <div className="space-y-0">
            <DetailRow label={intl.get("crossborder.detail.date")} value={transaction.time} />
            <DetailRow label={intl.get("crossborder.detail.origin")} value={originMethodLabel} />
            <DetailRow label={intl.get("crossborder.detail.destination")} value={destinationMethodLabel} />
            <DetailRow label={intl.get("crossborder.detail.originAccount")} value={originAccountLabel} />
            <DetailRow label={intl.get("crossborder.detail.valutAccount")} value={accountLabel} />
            <DetailRow label={intl.get("crossborder.detail.recipient")} value={transaction.senderCustomer} />
          </div>
        </section>

        <section className="flex flex-col gap-4">
          <div>
            <h4 className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-brand-dark dark:text-gray-100">
              {intl.get("crossborder.detail.amounts")}
            </h4>
            <div className="space-y-0">
              <DetailRow label={intl.get("crossborder.detail.originAmount")} value={senderAmount} />
              <DetailRow label={intl.get("crossborder.detail.destinationAmount")} value={receiverAmount} />
              <DetailRow label={intl.get("crossborder.detail.fee")} value={operationFeeLabel} />
              {transaction.status ? (
                <DetailRow
                  label={intl.get("crossborder.detail.status")}
                  value={<CrossborderStatusBadge status={transaction.status} />}
                />
              ) : null}
            </div>
          </div>

          {transaction.tracking?.length ? (
            <div className="border-t border-gray-100 pt-3 dark:border-brand-dark-border">
              <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-brand-dark dark:text-gray-100">
                {intl.get("crossborder.detail.tracking")}
              </h4>
              <CrossborderTrackingTimeline steps={transaction.tracking} />
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
};

const PayoutDetailBody = ({ transaction }) => {
  const { senderAmount, receiverAmount, operationFeeLabel } = useTransactionAmounts(transaction);
  const countryCode = transaction.countryCode ?? transaction.destinationCountryCode;

  return (
    <div className="flex-1 overflow-y-auto px-4 py-3 sm:px-5">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
        <section>
          <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-brand-dark dark:text-gray-100">
            {intl.get("crossborder.detail.payoutDetails")}
          </h4>
          <div className="rounded-xl border border-gray-100 bg-gray-50/40 px-3 dark:border-brand-dark-border dark:bg-brand-dark-elevated/30 sm:px-4">
            <DetailTableRow label={intl.get("crossborder.detail.date")} value={transaction.time} />
            <DetailTableRow
              label={intl.get("crossborder.detail.blockchain")}
              value={transaction.blockchain ?? transaction.network}
            />
            <DetailTableRow
              label={intl.get("crossborder.detail.senderAddress")}
              value={transaction.senderAddress}
              mono
            />
            <DetailTableRow label={intl.get("crossborder.detail.customer")} value={transaction.senderCustomer} />
            <DetailTableRow
              label={intl.get("crossborder.detail.country")}
              value={
                <CountryValue countryCode={countryCode} countryLabel={transaction.country} />
              }
            />
            <DetailTableRow
              label={intl.get("crossborder.detail.transferType")}
              value={
                <PaymentRailChip
                  railId={transaction.destinationRailId}
                  currencyId={transaction.toCurrency}
                />
              }
            />
            {transaction.pixKey ? (
              <DetailTableRow
                label={intl.get("crossborder.detail.pixKey")}
                value={transaction.pixKey}
                mono
              />
            ) : null}
            <DetailTableRow label={intl.get("crossborder.detail.senderAmount")} value={senderAmount} />
            <DetailTableRow label={intl.get("crossborder.detail.receiverAmount")} value={receiverAmount} />
            <DetailTableRow label={intl.get("crossborder.detail.fee")} value={operationFeeLabel} />
            {transaction.status ? (
              <DetailTableRow
                label={intl.get("crossborder.detail.status")}
                value={<CrossborderStatusBadge status={transaction.status} />}
              />
            ) : null}
          </div>
        </section>

        {transaction.tracking?.length ? (
          <section>
            <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-brand-dark dark:text-gray-100">
              {intl.get("crossborder.detail.payoutTracking")}
            </h4>
            <CrossborderTrackingTimeline steps={transaction.tracking} variant="compact" />
          </section>
        ) : null}
      </div>
    </div>
  );
};

export const CrossborderTransactionDetail = ({ transaction, onClose, className = "" }) => {
  if (!transaction) {
    return (
      <div className={`app-card flex min-h-[240px] items-center justify-center p-4 ${className}`}>
        <p className="text-sm text-gray-400">{intl.get("crossborder.detail.selectTransaction")}</p>
      </div>
    );
  }

  const isPayout = transaction.type === "payout";

  return (
    <div className={`app-card flex flex-col overflow-hidden ${className}`}>
      <TransactionHeader transaction={transaction} onClose={onClose} />
      {isPayout ? (
        <PayoutDetailBody transaction={transaction} />
      ) : (
        <PayinDetailBody transaction={transaction} />
      )}
      <DetailFooter onClose={onClose} />
    </div>
  );
};
